import { z } from "zod";
import { isValidPhoneNumber } from "react-phone-number-input";
import { format } from "date-fns";
import { eventTypeTypeValues } from "@ac-expo/api-types";
import { defaultPlaceValues, placeSchema } from "./event-place.schema";
import {
  invalidPlatformUrl,
  invalidUrl,
  validatePlatformUrl,
} from "../structure";

export const createEventSchema = z.object({
  name: z.string().min(1, "Le nom de l'événement est requis"),
  type: z.enum([...eventTypeTypeValues]),
  place: placeSchema,
  dates: z
    .object({
      from: z
        .date()
        .refine((date) => !isNaN(date.getTime()), "Date de début invalide"),
      to: z
        .date()
        .refine((date) => !isNaN(date.getTime()), "Date de fin invalide"),
    })
    .refine(
      (data) =>
        format(data.from, "yyyy-MM-dd") <= format(data.to, "yyyy-MM-dd"),
      {
        message: "La date de début doit être avant la date de fin",
        path: ["from"],
      }
    ),
  organiserEmail: z.string().email("Email invalide"),
  organiserPhone: z
    .string()
    .refine((phone) => isValidPhoneNumber(phone, "FR"), {
      message: "Téléphone invalide",
    }),
  entry: z
    .enum(["free", "paid"], {
      required_error: "Veuillez sélectionner un type d'entrée",
    })
    .default("free"),
  venue: z
    .object({
      indoor: z.boolean(),
      outdoor: z.boolean(),
    })
    .refine((data) => data.indoor || data.outdoor, {
      message: "Au moins un type de lieu doit être sélectionné",
    }),
  isAccepted: z.boolean().optional(),
});

export const defaultCreateEventValues = {
  name: "",
  type: undefined,
  place: defaultPlaceValues,
  dates: {
    from: new Date(),
    to: new Date(),
  },
  organiserEmail: "",
  organiserPhone: "",
  entry: "free" as const,
  venue: {
    indoor: false,
    outdoor: false,
  },
} satisfies Partial<CreateEventForm>;

export type CreateEventForm = z.infer<typeof createEventSchema>;

const urlOrEmptyString = z.union([z.string().url(), z.string().length(0)]);

export const createEventFromCsvSchema = z.object({
  name: z.string().min(1, "Le nom de l'événement est requis"),
  organiser: z.string().nullable().optional(),
  venue: z.enum(["Interieur", "Exterieur"] as const),
  entry: z.enum(["Payant", "Gratuit"] as const),
  websiteUrl: urlOrEmptyString.nullable().optional(),
  description: z.string().nullable().optional(),
  address: z.string().min(1, "L'adresse est requise"),
  place: z.string().min(1, "Le nom du lieu est requis"),
  type: z.enum(eventTypeTypeValues, {
    message: "Le type d'événement n'est pas valide",
  }),
  linkedinUrl: z
    .string()
    .url(invalidUrl)
    .refine(validatePlatformUrl(["linkedin.com"]), {
      message: invalidPlatformUrl("LinkedIn"),
    })
    .or(z.literal(""))
    .nullish(),
  facebookUrl: z
    .string()
    .url(invalidUrl)
    .refine(validatePlatformUrl(["facebook.com"]), {
      message: invalidPlatformUrl("Facebook"),
    })
    .or(z.literal(""))
    .nullish(),
  twitterUrl: z
    .string()
    .url(invalidUrl)
    .refine(validatePlatformUrl(["twitter.com", "x.com"]), {
      message: invalidPlatformUrl("X (Twitter)"),
    })
    .or(z.literal(""))
    .nullish(),
  instagramUrl: z
    .string()
    .url(invalidUrl)
    .refine(validatePlatformUrl(["instagram.com"]), {
      message: invalidPlatformUrl("Instagram"),
    })
    .or(z.literal(""))
    .nullish(),
  youtubeUrl: z
    .string()
    .url(invalidUrl)
    .refine(validatePlatformUrl(["youtube.com"]), {
      message: invalidPlatformUrl("YouTube"),
    })
    .or(z.literal(""))
    .nullish(),
  dates: z.array(z.date()).min(1),
  schedules: z.array(z.string().min(1, "Les horaires sont requis")),
});

export type CreateEventFromCsvSchema = z.infer<typeof createEventFromCsvSchema>;
